// src/components/modals/DownloadProgressModal.js
import React from 'react';

/**
 * Modal component to show download progress
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Object} props.progress - Progress object {current, total, currentInvoice}
 * @param {Array} props.errors - Array of error objects
 * @param {Object} props.result - Final result object
 * @param {Function} props.onClose - Close handler
 */
export default function DownloadProgressModal({ isOpen, progress, errors, result, onClose }) {
  if (!isOpen) return null;

  const percentage = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  const isComplete = result !== null;
  const hasErrors = errors && errors.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" dir="rtl">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">

          {/* Header */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">
              {isComplete ? 'הורדה הושלמה' : 'מוריד חשבוניות...'}
            </h3>
          </div>

          {/* Progress Bar */}
          {!isComplete && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{progress.total} / {progress.current}</span>
                <span>{percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Current Invoice */}
          {!isComplete && progress.currentInvoice && (
            <div className="mb-6 p-4 bg-purple-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-1">מוריד כעת:</p>
              <p className="text-sm font-medium text-gray-800 truncate">
                {progress.currentInvoice.customerName} - {progress.currentInvoice.serviceName}
              </p>
            </div>
          )}

          {/* Completion Summary */}
          {isComplete && (
            <div className="mb-6">
              {/* Success Message */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">הורדו בהצלחה:</span>
                  <span className="text-lg font-bold text-green-600">
                    {result.successCount} / {result.totalCount}
                  </span>
                </div>

                {hasErrors && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">נכשלו:</span>
                    <span className="text-lg font-bold text-red-600">
                      {errors.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Error List */}
              {hasErrors && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    חשבוניות שנכשלו:
                  </p>
                  <ul className="text-xs text-red-700 space-y-1">
                    {errors.map((err, idx) => (
                      <li key={idx} className="truncate">
                        • {err.invoice?.customerName || 'לא ידוע'} - {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Spinner */}
          {!isComplete && (
            <div className="flex justify-center mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          )}

          {/* Close Button */}
          {isComplete && (
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
            >
              סגור
            </button>
          )}

          {/* Note while downloading */}
          {!isComplete && (
            <p className="text-xs text-center text-gray-500">
              אנא המתן, ההורדה מתבצעת...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
